import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Target,
  DollarSign,
  Users,
  TrendingUp,
  BarChart3,
  MapPin,
  Phone,
  Mail,
  Globe,
  Tag
} from 'lucide-react'
import { Breadcrumb } from '../../components/Common'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import leadSourceMasterService from '../../services/leadSourceMasterService'

const LeadSourceMasterEdit = () => {
  const navigate = useNavigate()
  const { id } = useParams()
  const [activeTab, setActiveTab] = useState('leadSourceInfo')
  const [loading, setLoading] = useState(true)
  const [formData, setFormData] = useState({
    leadSourceCode: '',
    leadSourceName: '',
    leadSourceDescription: '',
    leadSourceCategory: 'Digital Marketing',
    leadSourceType: 'Online',
    leadSourceStatus: 'Active',
    isLeadSourceActive: 'A',
    costPerLead: 0,
    expectedConversionRate: 0,
    averageDealValue: 0,
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    targetRegions: [],
    targetIndustries: [],
    targetCompanySize: 'All',
    totalLeadsGenerated: 0,
    totalLeadsConverted: 0,
    totalRevenue: 0,
    lastLeadDate: '',
    trackingCode: '',
    utmSource: '',
    utmMedium: '',
    utmCampaign: '',
    leadQualityScore: 5,
    leadVolumeScore: 5,
    costEffectivenessScore: 5,
    notes: '',
    tags: []
  })

  const [targetRegions, setTargetRegions] = useState([''])
  const [targetIndustries, setTargetIndustries] = useState([''])
  const [tags, setTags] = useState([''])

  useEffect(() => {
    loadLeadSource()
  }, [id])

  const loadLeadSource = async () => {
    try {
      setLoading(true)
      const response = await leadSourceMasterService.getLeadSourceById(id)
      
      if (response.success) {
        const leadSource = response.data
        setFormData(leadSource)
        
        // Set arrays for dynamic fields
        setTargetRegions(leadSource.targetRegions?.length > 0 ? leadSource.targetRegions : [''])
        setTargetIndustries(leadSource.targetIndustries?.length > 0 ? leadSource.targetIndustries : [''])
        setTags(leadSource.tags?.length > 0 ? leadSource.tags : [''])
      } else {
        toast.error(response.message || 'Failed to load lead source')
        navigate('/crm/lead-source-master')
      }
    } catch (error) {
      console.error('Error loading lead source:', error)
      toast.error('Failed to load lead source')
      navigate('/crm/lead-source-master')
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleTargetRegionChange = (index, value) => {
    const newRegions = [...targetRegions]
    newRegions[index] = value
    setTargetRegions(newRegions)
    setFormData(prev => ({
      ...prev,
      targetRegions: newRegions.filter(region => region.trim() !== '')
    }))
  }

  const addTargetRegion = () => {
    setTargetRegions([...targetRegions, ''])
  }

  const removeTargetRegion = (index) => {
    if (targetRegions.length > 1) {
      const newRegions = targetRegions.filter((_, i) => i !== index)
      setTargetRegions(newRegions)
      setFormData(prev => ({
        ...prev,
        targetRegions: newRegions.filter(region => region.trim() !== '')
      }))
    }
  }

  const handleTargetIndustryChange = (index, value) => {
    const newIndustries = [...targetIndustries]
    newIndustries[index] = value
    setTargetIndustries(newIndustries)
    setFormData(prev => ({
      ...prev,
      targetIndustries: newIndustries.filter(industry => industry.trim() !== '')
    }))
  }

  const addTargetIndustry = () => {
    setTargetIndustries([...targetIndustries, ''])
  }

  const removeTargetIndustry = (index) => {
    if (targetIndustries.length > 1) {
      const newIndustries = targetIndustries.filter((_, i) => i !== index)
      setTargetIndustries(newIndustries)
      setFormData(prev => ({
        ...prev,
        targetIndustries: newIndustries.filter(industry => industry.trim() !== '')
      }))
    }
  }

  const handleTagChange = (index, value) => {
    const newTags = [...tags]
    newTags[index] = value
    setTags(newTags)
    setFormData(prev => ({
      ...prev,
      tags: newTags.filter(tag => tag.trim() !== '')
    }))
  }

  const addTag = () => {
    setTags([...tags, ''])
  }

  const removeTag = (index) => {
    if (tags.length > 1) {
      const newTags = tags.filter((_, i) => i !== index)
      setTags(newTags)
      setFormData(prev => ({
        ...prev,
        tags: newTags.filter(tag => tag.trim() !== '')
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    try {
      const submissionData = {
        ...formData,
        targetRegions: formData.targetRegions.filter(region => region.trim() !== ''),
        targetIndustries: formData.targetIndustries.filter(industry => industry.trim() !== ''),
        tags: formData.tags.filter(tag => tag.trim() !== '')
      }

      const response = await leadSourceMasterService.updateLeadSource(id, submissionData)
      
      if (response.success) {
        toast.success('Lead source updated successfully')
        navigate('/crm/lead-source-master')
      } else {
        toast.error(response.message || 'Failed to update lead source')
      }
    } catch (error) {
      console.error('Error updating lead source:', error)
      toast.error('Failed to update lead source')
    }
  }

  const handleBack = () => {
    navigate('/crm/lead-source-master')
  }

  const breadcrumbItems = [
    {
      label: 'CRM',
      path: '/crm',
      onClick: () => navigate('/crm')
    },
    {
      label: 'Lead Source Master',
      path: '/crm/lead-source-master',
      onClick: () => navigate('/crm/lead-source-master')
    },
    {
      label: 'Edit Lead Source',
      path: `/crm/lead-source-master/edit/${id}`,
      isCurrent: true
    }
  ]

  if (loading) {
    return (
      <div className="sales-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading lead source...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Edit Lead Source</h1>
          <p>Update lead source information</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-secondary"
            onClick={handleBack}
          >
            <ArrowLeft size={16} />
            Back to Lead Sources
          </button>
        </div>
      </div>

      {/* Form Section */}
      <div className="form-container">
        <div className="sac-form-header">
          <h2>Lead Source Master Entry</h2>
        </div>
        
        {/* Tab Navigation */}
        <div className="tab-navigation">
          <button
            className={`tab-button ${activeTab === 'leadSourceInfo' ? 'active' : ''}`}
            onClick={() => setActiveTab('leadSourceInfo')}
          >
            <Target size={18} />
            <span>Lead Source Info</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'performance' ? 'active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            <BarChart3 size={18} />
            <span>Performance Metrics</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'tracking' ? 'active' : ''}`}
            onClick={() => setActiveTab('tracking')}
          >
            <Globe size={18} />
            <span>Tracking & Integration</span>
          </button>
          <button
            className={`tab-button ${activeTab === 'targeting' ? 'active' : ''}`}
            onClick={() => setActiveTab('targeting')}
          >
            <Users size={18} />
            <span>Targeting & Demographics</span>
          </button>
        </div>
          
        <form className="form-content">
          {/* Same form content as LeadSourceMasterForm but with pre-filled data */}
          {/* Lead Source Info Tab */}
          {activeTab === 'leadSourceInfo' && (
            <div className="tab-content">
              <div className="section-header">
                <h3>Lead Source Information</h3>
              </div>
              
              {/* Row 1: Lead Source Code, Name, Category, Type */}
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="leadSourceCode" className="form-label required">
                    Lead Source Code
                  </label>
                  <input
                    type="text"
                    id="leadSourceCode"
                    name="leadSourceCode"
                    value={formData.leadSourceCode}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="leadSourceName" className="form-label required">
                    Lead Source Name
                  </label>
                  <input
                    type="text"
                    id="leadSourceName"
                    name="leadSourceName"
                    value={formData.leadSourceName}
                    onChange={(e) => handleInputChange('leadSourceName', e.target.value)}
                    className="form-input"
                    placeholder="Enter lead source name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="leadSourceCategory" className="form-label required">
                    Category
                  </label>
                  <select
                    id="leadSourceCategory"
                    name="leadSourceCategory"
                    value={formData.leadSourceCategory}
                    onChange={(e) => handleInputChange('leadSourceCategory', e.target.value)}
                    className="form-input"
                  >
                    <option value="Digital Marketing">Digital Marketing</option>
                    <option value="Traditional Marketing">Traditional Marketing</option>
                    <option value="Referral">Referral</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Event">Event</option>
                    <option value="Cold Outreach">Cold Outreach</option>
                    <option value="Content Marketing">Content Marketing</option>
                    <option value="Social Media">Social Media</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="leadSourceType" className="form-label required">
                    Type
                  </label>
                  <select
                    id="leadSourceType"
                    name="leadSourceType"
                    value={formData.leadSourceType}
                    onChange={(e) => handleInputChange('leadSourceType', e.target.value)}
                    className="form-input"
                  >
                    <option value="Online">Online</option>
                    <option value="Offline">Offline</option>
                    <option value="Hybrid">Hybrid</option>
                  </select>
                </div>
              </div>

              {/* Additional form fields would go here - same structure as LeadSourceMasterForm */}
            </div>
          )}

          {/* Other tabs would have similar structure */}

          {/* Action Buttons */}
          <div className="form-actions">
            <button
              type="button"
              onClick={handleBack}
              className="btn-secondary"
            >
              <ArrowLeft size={16} />
              Cancel
            </button>
            <button
              type="submit"
              onClick={handleSubmit}
              className="btn-primary"
            >
              <Save size={16} />
              Update
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default LeadSourceMasterEdit
